import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { EditLayer, Field } from '@/admin/AdminShell'
import { Icon } from '@/components/Icon'
import { StateView, type HeaderAction } from '@/card/record'
import { ApiError } from '@/lib/api'
import { ReplyDraftButton } from '@/ai/ReplyDraft'
import {
  composeMail,
  fetchTemplatesFor,
  retryMail,
  sendMail,
  type MailDraft,
  type MailMessage,
  type MailRecordType,
  type ReplyTarget,
} from './api'

/**
 * «Написать письмо» на карточке (тикет #117).
 *
 * Карточка получает кнопку и окно одним хуком и ничего не знает о почте: так четыре карточки
 * (сделка, контрагент, контакт, счёт) не повторяют форму и не расходятся в том, что
 * отправляют.
 *
 * Итог отправки называется словами в самом окне: «отправлено», «в очереди повтора» или
 * причина отказа. Окно, которое просто закрылось, читается как «ушло» — даже когда почта не
 * подключена и письмо записано в ленту с отказом.
 */
export interface WriteEmailTarget {
  type: MailRecordType
  id: string
}

export function useWriteEmail(target: WriteEmailTarget): { action: HeaderAction | null; dialog: ReactNode } {
  const { t, user } = useApp()
  const [open, setOpen] = useState(false)
  // Право писать — право вести активности записи; видимость самой записи проверяет сервер.
  const allowed = can(user, 'sales.activity', 'create')
  return {
    action: allowed
      ? { id: 'write-email', label: t('mail.write'), icon: 'mail', onClick: () => setOpen(true) }
      : null,
    dialog: open ? <WriteEmailDialog target={target} onClose={() => setOpen(false)} /> : null,
  }
}

const newKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

export function WriteEmailDialog({ target, reply, onClose }: {
  target: WriteEmailTarget
  /** Ответ на входящее письмо (#134): адрес и тема заполнены, есть «Черновик ответа». */
  reply?: ReplyTarget
  onClose: () => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const formId = useId()
  const listId = useId()
  // Born when the form opens, not when it is sent: a lost answer is replayed under it.
  const [idempotencyKey] = useState(newKey)

  const [templateId, setTemplateId] = useState<string>('')
  const [to, setTo] = useState(reply?.to ?? '')
  const [subject, setSubject] = useState(reply?.subject ?? '')
  const [body, setBody] = useState('')
  const [draftTaskId, setDraftTaskId] = useState<string | null>(null)
  const [files, setFiles] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<MailMessage | null>(null)

  const templates = useQuery({
    queryKey: ['mail-templates', target.type],
    queryFn: ({ signal }) => fetchTemplatesFor(target.type, signal),
    retry: false,
  })
  const draft = useQuery({
    queryKey: ['mail-draft', target.type, target.id, templateId],
    queryFn: ({ signal }) => composeMail(target.type, target.id, templateId || null, signal),
    retry: false,
  })

  // A chosen template replaces subject and body; recipients and files are offered once.
  useEffect(() => {
    const data: MailDraft | undefined = draft.data
    if (!data) return
    if (templateId) {
      setSubject(data.subject)
      setBody(data.body)
    }
    setTo((current) => current || data.recipients[0]?.email || '')
    setFiles((current) =>
      current.length > 0 ? current : data.attachments.filter((file) => file.invoicePdf && file.available).map((file) => file.id),
    )
  }, [draft.data, templateId])

  const send = useMutation({
    mutationFn: () =>
      sendMail(
        {
          entityType: target.type,
          entityId: target.id,
          templateId: templateId || null,
          to: to.trim(),
          subject,
          body,
          attachmentIds: files,
          replyToInboundId: reply?.inboundId ?? null,
          draftTaskId,
        },
        idempotencyKey,
      ),
    onSuccess: (message) => {
      setResult(message)
      // The letter is in the feed whatever happened to it: the record's and the customer's.
      void queryClient.invalidateQueries({ queryKey: ['timeline'] })
      void queryClient.invalidateQueries({ queryKey: ['activities', target.type, target.id] })
    },
    onError: (failure) => setError(failure instanceof ApiError ? failure.message : t('app.error')),
  })

  const missing = draft.data?.missing ?? []
  const connected = draft.data?.mailConnected ?? true
  const canSend = to.trim() !== '' && subject.trim() !== '' && body.trim() !== '' && !send.isPending
  const recipients = useMemo(() => draft.data?.recipients ?? [], [draft.data])

  if (result) {
    return (
      <EditLayer kind="modal" large={false} title={t('mail.result.title')} onClose={onClose}
                 footer={<button type="button" className="btn btn-primary" onClick={onClose}>{t('mail.close')}</button>}>
        <MailResult message={result} onRetried={setResult} />
      </EditLayer>
    )
  }

  return (
    <EditLayer
      kind="sheet"
      wide
      overline={t(`mail.record.${target.type}`)}
      title={t('mail.write')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('admin.cancel')}</button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={!canSend}>
            <Icon name="mail" />
            {send.isPending ? t('mail.sending') : t('mail.send')}
          </button>
        </>
      }
    >
      {draft.error ? (
        <StateView kind={draft.error instanceof ApiError && draft.error.isForbidden ? 'forbidden' : 'error'}
                   title={t('mail.draft.error')} description={draft.error instanceof Error ? draft.error.message : undefined} />
      ) : (
        <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('mail.form')} onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          if (canSend) send.mutate()
        }}>
          {!connected && (
            <div className="alert alert-warning" role="note">
              <Icon name="alert" />
              <span>{t('mail.notConnected')}</span>
            </div>
          )}
          <Field label={t('mail.template')} htmlFor={`${formId}-template`}
                 help={(templates.data ?? []).length === 0 && !templates.isLoading ? t('mail.template.none') : undefined}>
            <select id={`${formId}-template`} className="input" value={templateId}
                    onChange={(event) => setTemplateId(event.target.value)}>
              <option value="">{t('mail.template.blank')}</option>
              {(templates.data ?? []).map((template) => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </select>
          </Field>
          <Field label={t('mail.to')} htmlFor={`${formId}-to`} required help={t('mail.to.help')}>
            <input id={`${formId}-to`} className="input" type="email" list={listId} value={to}
                   onChange={(event) => setTo(event.target.value)} />
            <datalist id={listId}>
              {recipients.map((recipient) => (
                <option key={recipient.email} value={recipient.email}>{recipient.name ?? recipient.email}</option>
              ))}
            </datalist>
          </Field>
          {recipients.length > 0 && (
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }} aria-label={t('mail.recipients')}>
              {recipients.map((recipient) => (
                <button key={recipient.email} type="button" className="btn btn-ghost btn-sm"
                        aria-pressed={to.trim() === recipient.email} onClick={() => setTo(recipient.email)}>
                  <Icon name="user" size="sm" />
                  {recipient.name ? `${recipient.name} · ${recipient.email}` : recipient.email}
                </button>
              ))}
            </div>
          )}
          <Field label={t('mail.subject')} htmlFor={`${formId}-subject`} required>
            <input id={`${formId}-subject`} className="input" maxLength={512} value={subject}
                   onChange={(event) => setSubject(event.target.value)} />
          </Field>
          {reply && (
            <ReplyDraftButton channel="email" sourceId={reply.inboundId} onDraft={(text, taskId) => {
              setBody(text)
              setDraftTaskId(taskId)
            }} />
          )}
          <Field label={t('mail.body')} htmlFor={`${formId}-body`} required>
            <textarea id={`${formId}-body`} className="input" rows={10} value={body}
                      onChange={(event) => setBody(event.target.value)} />
          </Field>
          {missing.length > 0 && (
            <div className="alert alert-warning" role="note">
              <Icon name="alert" />
              <span>{t('mail.missing', { list: missing.join(', ') })}</span>
            </div>
          )}
          {(draft.data?.attachments ?? []).length > 0 && (
            <fieldset className="stack" style={{ gap: 6, border: 0, padding: 0, margin: 0 }}>
              <legend className="label">{t('mail.attachments')}</legend>
              {(draft.data?.attachments ?? []).map((file) => (
                <label key={file.id} className="row" style={{ gap: 8 }}>
                  <input type="checkbox" disabled={!file.available} checked={files.includes(file.id)}
                         onChange={(event) =>
                           setFiles((current) => event.target.checked ? [...current, file.id] : current.filter((id) => id !== file.id))} />
                  <Icon name="copy" size="sm" />
                  <span>{file.fileName}</span>
                  {file.invoicePdf && <span className="badge">{t('mail.attachment.invoicePdf')}</span>}
                  {!file.available && (
                    <span className="t-sm muted">{t(`mail.attachment.unavailable.${file.unavailableReason ?? 'missing'}`)}</span>
                  )}
                </label>
              ))}
            </fieldset>
          )}
          {error && (
            <div className="alert alert-danger" role="alert">
              <Icon name="alert" />
              <span>{error}</span>
            </div>
          )}
        </form>
      )}
    </EditLayer>
  )
}

/** Итог отправки словами: то же, что потом покажет лента. */
export function MailResult({ message, onRetried }: { message: MailMessage; onRetried?: (message: MailMessage) => void }) {
  const { t, locale } = useApp()
  const queryClient = useQueryClient()
  // #94: a letter waiting for its scheduled retry can be retried now. The server takes it
  // only from `pending|failed` to `sending`, so a double click does not send it twice.
  const retry = useMutation({
    mutationFn: () => retryMail(message.id),
    onSuccess: (next) => {
      onRetried?.(next)
      void queryClient.invalidateQueries({ queryKey: ['timeline'] })
    },
  })
  if (message.status === 'sent') {
    return (
      <div className="alert alert-success" role="status">
        <Icon name="check" />
        <span>{t('mail.result.sent', { to: message.to })}</span>
      </div>
    )
  }
  if (message.status === 'failed') {
    const when = message.retryAfter ? new Date(message.retryAfter).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU') : ''
    return (
      <div className="alert alert-warning" role="status">
        <Icon name="clock" />
        <span>{t('mail.result.failed', { when })}{message.reason ? ` ${message.reason}` : ''}</span>
        {onRetried && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={retry.isPending} onClick={() => retry.mutate()}>
            {t('mail.result.retryNow')}
          </button>
        )}
        {retry.error ? <span role="alert">{retry.error instanceof ApiError ? retry.error.message : t('app.error')}</span> : null}
      </div>
    )
  }
  if (message.status === 'rejected') {
    return (
      <div className="alert alert-danger" role="alert">
        <Icon name="alert" />
        <span>{t('mail.result.rejected', { reason: message.reason ?? '' })}</span>
      </div>
    )
  }
  return (
    <div className="alert alert-warning" role="status">
      <Icon name="clock" />
      <span>{t('mail.result.pending')}</span>
    </div>
  )
}
